import {
  ActionIcon,
  Card,
  Group,
  Menu,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconDotsVertical, IconInfoCircle } from '@tabler/icons-react';
import type { ReactNode, RefObject } from 'react';
import { useFadeIn } from '../hooks/useFadeIn';

export interface ChartCardMenuItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
}

export interface ChartCardProps {
  title: string;
  definition?: string;
  dataSource?: string;
  /** Shown first in the title (ⓘ) tooltip, e.g. sorting or interaction hints. */
  infoTooltipLead?: ReactNode;
  menuItems?: ChartCardMenuItem[];
  onClick?: () => void;
  headerRight?: ReactNode;
  children: ReactNode;
}

function buildInfoContent(
  definition?: string,
  dataSource?: string,
  infoTooltipLead?: ReactNode,
): ReactNode {
  if (!definition && !dataSource && !infoTooltipLead) return null;
  return (
    <Stack gap="sm" maw={280}>
      {infoTooltipLead}
      {definition && <Text size="sm">{definition}</Text>}
      {dataSource && (
        <Text size="xs" c="dimmed">
          Source:
          {' '}
          {dataSource}
        </Text>
      )}
    </Stack>
  );
}

export function ChartCard({
  title,
  definition,
  dataSource,
  infoTooltipLead,
  menuItems,
  onClick,
  headerRight,
  children,
}: ChartCardProps): JSX.Element {
  const infoContent = buildInfoContent(definition, dataSource, infoTooltipLead);
  const showInfo = Boolean(infoContent);
  const { ref, style: fadeStyle } = useFadeIn<HTMLDivElement>();

  return (
    <Card
      ref={ref as RefObject<HTMLDivElement>}
      withBorder
      padding="lg"
      radius="md"
      shadow="xs"
      style={{
        cursor: onClick ? 'pointer' : undefined,
        ...fadeStyle,
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = 'var(--mantine-shadow-xs)';
        }
      }}
    >
      <Stack gap="md">
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
            <Text fw={600} size="md" lineClamp={2}>
              {title}
            </Text>
            {showInfo && (
              <Tooltip
                label={infoContent}
                multiline
                w={300}
                position="bottom-start"
                events={{ hover: true, focus: true, touch: true }}
              >
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Metric information"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconInfoCircle size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          <Group gap="xs" wrap="nowrap">
            {headerRight}
            {menuItems && menuItems.length > 0 && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Chart actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                  {menuItems.map((item) => (
                    <Menu.Item
                      key={item.label}
                      leftSection={item.icon}
                      onClick={(e) => {
                        e.stopPropagation();
                        item.onClick();
                      }}
                    >
                      {item.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>
        {children}
      </Stack>
    </Card>
  );
}
